import React, { useEffect, useRef, useState } from 'react';
import { Globe } from '@phosphor-icons/react';
import { useTranslation } from '../../i18n/I18nContext';
import './LanguageSelector.css';

/**
 * LanguageSelector — компактный dropdown языков для хедера лендинга
 * (issue #1024). Показывает текущий 2-letter код, по тапу разворачивается
 * popover-меню со всеми поддерживаемыми языками (whitelist `fr/en/de/lu`).
 *
 * Дизайн в той же глассовой плашке, что и `landing-close-fab` — чтобы
 * читались как единый ряд в правом верхнем углу. Закрывается на Esc и
 * клик-вне-меню.
 */
export const LanguageSelector = ({ className = '' }) => {
  const { language, setLanguage, languages, languageMeta, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const currentMeta = languageMeta[language] || { label: language.toUpperCase(), title: language };

  return (
    <div
      ref={rootRef}
      className={`landing-language-selector ${className}`}
    >
      <button
        type="button"
        className="landing-language-selector__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('landing.language.select')}
        onClick={() => setOpen((v) => !v)}
      >
        <Globe
          className="landing-language-selector__icon"
          size={14}
          weight="regular"
          aria-hidden="true"
        />
        <span className="landing-language-selector__label">{currentMeta.label}</span>
      </button>
      {open && (
        <ul
          className="landing-language-selector__menu"
          role="listbox"
          aria-label={t('landing.language.select')}
        >
          {languages.map((lang) => {
            const meta = languageMeta[lang];
            const isActive = lang === language;
            return (
              <li key={lang} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={`landing-language-selector__option${isActive ? ' landing-language-selector__option--active' : ''}`}
                  onClick={() => {
                    setLanguage(lang);
                    setOpen(false);
                  }}
                  title={meta.title}
                >
                  <span className="landing-language-selector__option-code">{meta.label}</span>
                  <span className="landing-language-selector__option-name">{meta.title}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LanguageSelector;
